/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useState, useCallback, useMemo } from 'react';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type {
	ApplicationResponse,
	HelpCenterUrls,
	PropertiesService,
	CustomDomainStatus,
} from './HelpCenterUrls.types.tsx';

const USER_SEEN_FLAG_PROPERTY_KEY = 'jsm.custom.domain.flag.seen.date';
const PACKAGE_NAME = 'jiraHelpCenterCustomDomainChangeBoarding';
const TEAM_NAME = 'jsm-banana';
const FETCH_USER_PROPERTY_ERROR_ID = 'fetchUserProperty';
const UPDATE_USER_PROPERTY_ERROR_ID = 'updateUserProperty';
const GET_SHOW_FLAG_DATE_ERROR_ID = 'getShowFlagDate';

export const NOT_LOADED = 'NOT_LOADED';
export const NO_VALUE = 'NO_VALUE';

export type NotLoaded = typeof NOT_LOADED;
export type NoValue = typeof NO_VALUE;

const EXPECTED_ERROR_STATUS = [403, 404];

export const useProperties: PropertiesService = () => {
	// oxlint-disable-next-line react/react-compiler
	const currentTime = Date.now();
	const [loading, setLoading] = useState<boolean>(false);

	const [error, setError] = useState<Error | null>(null);
	const [showFlagUpdateStartDate, setShowFlagUpdateStartDate] = useState<
		number | NotLoaded | NoValue
	>(NOT_LOADED);
	const [helpCenterUrls, setHelpcenterUrls] = useState<HelpCenterUrls | undefined>(undefined);
	const [customDomainStatus, setCustomDomainStatus] = useState<CustomDomainStatus | undefined>(
		undefined,
	);
	const [userSeenFlagDate, setUserSeenFlagDate] = useState<number | NotLoaded | NoValue>(
		NOT_LOADED,
	);

	const { atlassianAccountId } = useTenantContext();

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const handleErrorLogging = useCallback((operation: string, e: any) => {
		setLoading(false);
		if (e.name === 'FetchError' && !EXPECTED_ERROR_STATUS.includes(e.statusCode)) {
			fireErrorAnalytics({
				meta: {
					id: operation,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error: e,
				sendToPrivacyUnsafeSplunk: true,
			});
			setError(e);
		}
	}, []);

	const fetchSeenFlagDateProperty = useCallback(async () => {
		if (loading) return;
		if (atlassianAccountId && userSeenFlagDate === NOT_LOADED) {
			setLoading(true);
			try {
				const response = await getUserProperty(atlassianAccountId, USER_SEEN_FLAG_PROPERTY_KEY);
				setLoading(false);
				setUserSeenFlagDate(Number(response));
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				setUserSeenFlagDate(NO_VALUE);
				handleErrorLogging(FETCH_USER_PROPERTY_ERROR_ID, e);
			}
		}
	}, [atlassianAccountId, handleErrorLogging, loading, userSeenFlagDate]);

	const updateUserProperty = useCallback(async () => {
		if (loading) return;
		if (atlassianAccountId) {
			setLoading(true);
			try {
				await setUserProperties(
					atlassianAccountId,
					USER_SEEN_FLAG_PROPERTY_KEY,
					currentTime.toString(),
				);
				setLoading(false);
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				handleErrorLogging(UPDATE_USER_PROPERTY_ERROR_ID, e);
			}
		}
	}, [atlassianAccountId, currentTime, handleErrorLogging, loading]);

	const fetchApplicationProperty = useCallback(async () => {
		if (loading) return;
		setLoading(true);
		try {
			const fetchStartTime = performance.now();

			const response: ApplicationResponse = await performGetRequest(
				'/rest/servicedesk/1/customdomain',
			);

			addSpanToAll(
				'fetch',
				'rest/servicedesk/1/customdomain',
				[{ name: 'network' }],
				fetchStartTime,
				performance.now(),
			);

			const { startDate, newUrl, oldUrl, status } = response;
			setLoading(false);
			setShowFlagUpdateStartDate(startDate);
			setHelpcenterUrls({ newUrl, oldUrl });
			setCustomDomainStatus(status);
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			setShowFlagUpdateStartDate(NO_VALUE);
			handleErrorLogging(GET_SHOW_FLAG_DATE_ERROR_ID, e);
		}
	}, [handleErrorLogging, loading]);

	const memoisedData = useMemo(
		() => ({
			userSeenFlagDate,
			showFlagUpdateStartDate,
			helpCenterUrls,
			customDomainStatus,
			loading,
			error,
		}),
		[userSeenFlagDate, showFlagUpdateStartDate, loading, error, helpCenterUrls, customDomainStatus],
	);
	return [
		memoisedData,
		{
			fetchSeenFlagDateProperty,
			updateUserProperty,
			fetchApplicationProperty,
		},
	];
};
